import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, CheckSquare, FilePdf, FileXls, UsersThree } from "@phosphor-icons/react";

// Çalışma tipi kodları — Türkçe kısa etiket için harita
const WT_META = {
  "tam_gun":   { code: "T",  color: "bg-emerald-500 text-white",  label: "Tam Gün" },
  "yarim_gun": { code: "Y",  color: "bg-amber-500 text-white",    label: "Yarım Gün" },
  "mesai":     { code: "M",  color: "bg-[#1B4776] text-white",     label: "Mesai" },
  "izinli":    { code: "İ",  color: "bg-blue-400 text-white",      label: "İzinli" },
  "raporlu":   { code: "R",  color: "bg-red-500 text-white",       label: "Raporlu" },
  "yok":       { code: "-",  color: "bg-white text-slate-300",     label: "Boş" },
};
const WT_ORDER = ["tam_gun", "yarim_gun", "mesai", "izinli", "raporlu", "yok"];

// Y-M-D verildiğinde o ayın gün sayısı
const daysInMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};

const today = () => new Date().toISOString().slice(0, 7);

export default function Timesheet() {
  const { sites, activeSiteId } = useSites();
  const [siteId, setSiteId] = useState("");
  const [month, setMonth] = useState(today());
  const [teamFilter, setTeamFilter] = useState("all");
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);   // list of entries for month + site
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeSiteId && activeSiteId !== "all") setSiteId(activeSiteId);
    else if (sites.length && !siteId) setSiteId(sites[0]?.id || "");
  }, [activeSiteId, sites]); // eslint-disable-line

  // Ekipleri bir kere yükle
  useEffect(() => {
    axios.get(`${API}/teams`)
      .then(({ data }) => setTeams(data))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  const load = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const empParams = { site_id: siteId, status: "aktif" };
      if (teamFilter !== "all") empParams.team_id = teamFilter;
      const [emp, ts] = await Promise.all([
        axios.get(`${API}/employees`, { params: empParams }),
        axios.get(`${API}/timesheet`, { params: { site_id: siteId, month } }),
      ]);
      setEmployees(emp.data);
      setEntries(ts.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [siteId, month, teamFilter]); // eslint-disable-line

  const downloadPdf = async () => {
    if (!siteId) { toast.error("Önce şantiye seçin"); return; }
    try {
      const params = { site_id: siteId, month };
      if (teamFilter !== "all") params.team_id = teamFilter;
      const res = await axios.get(`${API}/timesheet/pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const teamPart = teamFilter !== "all"
        ? "-" + (teams.find((t) => t.id === teamFilter)?.name || "").replace(/\s+/g, "_")
        : "";
      a.download = `puantaj-${site?.code || "sha"}-${month}${teamPart}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadXlsx = async () => {
    if (!siteId) { toast.error("Önce şantiye seçin"); return; }
    try {
      const params = { site_id: siteId, month };
      if (teamFilter !== "all") params.team_id = teamFilter;
      const res = await axios.get(`${API}/timesheet/xlsx`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const teamPart = teamFilter !== "all"
        ? "-" + (teams.find((t) => t.id === teamFilter)?.name || "").replace(/\s+/g, "_")
        : "";
      a.download = `puantaj-${site?.code || "sha"}-${month}${teamPart}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const dayCount = daysInMonth(month);
  const days = Array.from({ length: dayCount }, (_, i) => i + 1);
  const site = sites.find(s => s.id === siteId);

  // (employee_id, YYYY-MM-DD) => entry
  const map = useMemo(() => {
    const m = new Map();
    entries.forEach(e => m.set(`${e.employee_id}|${e.date}`, e));
    return m;
  }, [entries]);

  const cycleType = (current) => {
    const idx = WT_ORDER.indexOf(current);
    return WT_ORDER[(idx + 1) % WT_ORDER.length];
  };

  const saveCell = async (emp, dayNum, work_type) => {
    const date = `${month}-${String(dayNum).padStart(2, "0")}`;
    try {
      await axios.post(`${API}/timesheet`, {
        employee_id: emp.id, employee_name: emp.name,
        site_id: siteId, site_name: site?.name || "",
        date, work_type,
        hours: work_type === "yarim_gun" ? 4 : work_type === "tam_gun" || work_type === "mesai" ? 8 : 0,
        overtime_hours: work_type === "mesai" ? 3 : 0,
      });
      // update local
      if (work_type === "yok") {
        setEntries(prev => prev.filter(e => !(e.employee_id === emp.id && e.date === date)));
      } else {
        setEntries(prev => {
          const filtered = prev.filter(e => !(e.employee_id === emp.id && e.date === date));
          return [...filtered, { employee_id: emp.id, employee_name: emp.name, site_id: siteId, date, work_type,
                                 hours: work_type === "yarim_gun" ? 4 : 8, overtime_hours: work_type === "mesai" ? 3 : 0 }];
        });
      }
    } catch (e) { toast.error(formatErr(e)); }
  };

  const summary = useMemo(() => {
    const s = {};
    employees.forEach(e => { s[e.id] = { tam: 0, yarim: 0, mesai: 0, izin: 0, rapor: 0, hours: 0, ot: 0 }; });
    entries.forEach(e => {
      const st = s[e.employee_id];
      if (!st) return;
      if (e.work_type === "tam_gun") st.tam += 1;
      else if (e.work_type === "yarim_gun") st.yarim += 0.5;
      else if (e.work_type === "mesai") st.mesai += 1;
      else if (e.work_type === "izinli") st.izin += 1;
      else if (e.work_type === "raporlu") st.rapor += 1;
      st.hours += e.hours || 0;
      st.ot += e.overtime_hours || 0;
    });
    return s;
  }, [entries, employees]);

  return (
    <Shell title="Puantaj">
      {/* Filters */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Şantiye</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-64" data-testid="ts-site">
              <SelectValue placeholder="Şantiye seçin" />
            </SelectTrigger>
            <SelectContent>
              {sites.filter(s => s.status === "aktif").map(s => (
                <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ay</Label>
          <Input type="month" value={month} data-testid="ts-month"
                 onChange={e => setMonth(e.target.value)}
                 className="mt-1 rounded-none sm:rounded-sm w-40" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1">
            <UsersThree size={11} weight="fill" /> Ekip
          </Label>
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-56" data-testid="ts-team-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Ekipler</SelectItem>
              {teams.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px] flex items-end justify-end gap-2">
          <Button variant="outline" onClick={downloadXlsx} data-testid="ts-xlsx-btn"
                  className="rounded-none sm:rounded-sm border-slate-300">
            <FileXls size={14} className="mr-2 text-emerald-700" /> Excel İndir
          </Button>
          <Button onClick={downloadPdf} data-testid="ts-pdf-btn"
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
            <FilePdf size={14} className="mr-2" /> PDF İndir
          </Button>
        </div>
      </Card>

      {/* Hint */}
      <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
        <Calendar size={14} weight="fill" /> Hücreye tıklayarak tipi değiştirin
      </div>

      {/* Legend */}
      <div className="mb-3 flex flex-wrap gap-2">
        {WT_ORDER.filter(k => k !== "yok").map(k => (
          <div key={k} className="flex items-center gap-1.5 text-xs">
            <span className={`inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold ${WT_META[k].color}`}>
              {WT_META[k].code}
            </span>
            <span className="text-slate-600">{WT_META[k].label}</span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden" data-testid="ts-grid">
        {!siteId ? (
          <div className="py-16 text-center text-slate-500">Lütfen şantiye seçin.</div>
        ) : loading ? (
          <div className="py-16 text-center text-slate-500">Yükleniyor...</div>
        ) : employees.length === 0 ? (
          <div className="py-16 text-center text-slate-500">Bu şantiyeye atanmış aktif personel yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 bg-slate-50 z-10 text-left px-3 py-2 font-bold uppercase tracking-widest text-[9px] min-w-[180px]">
                    Personel
                  </th>
                  {days.map(d => {
                    const date = new Date(`${month}-${String(d).padStart(2, "0")}`);
                    const wd = date.getDay();
                    const isWeekend = wd === 0 || wd === 6;
                    return (
                      <th key={d} className={`text-center px-1 py-2 font-bold w-8 ${isWeekend ? "bg-slate-100 text-slate-500" : ""}`}>
                        {d}
                      </th>
                    );
                  })}
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-widest text-[9px] bg-slate-100 border-l-2 border-slate-300">Tam</th>
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-widest text-[9px] bg-slate-100">Mesai</th>
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-widest text-[9px] bg-slate-100">İzin</th>
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-widest text-[9px] bg-slate-100">Rapor</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => (
                  <tr key={emp.id} className={idx % 2 ? "bg-slate-50/40" : ""} data-testid={`ts-emp-${idx}`}>
                    <td className="sticky left-0 z-10 border-r border-slate-200 px-3 py-1.5"
                        style={{ background: idx % 2 ? "rgba(248,250,252,0.95)" : "#fff" }}>
                      <div className="font-medium text-slate-900">{emp.name}</div>
                      <div className="text-[10px] text-slate-500">{emp.position}</div>
                    </td>
                    {days.map(d => {
                      const date = `${month}-${String(d).padStart(2, "0")}`;
                      const entry = map.get(`${emp.id}|${date}`);
                      const wt = entry?.work_type || "yok";
                      const meta = WT_META[wt];
                      return (
                        <td key={d} className="p-0.5">
                          <button
                            type="button"
                            data-testid={`ts-cell-${idx}-${d}`}
                            onClick={() => saveCell(emp, d, cycleType(wt))}
                            title={meta.label}
                            className={`w-7 h-7 text-[10px] font-bold border border-slate-200 hover:opacity-80 transition ${meta.color}`}
                          >
                            {meta.code === "-" ? "" : meta.code}
                          </button>
                        </td>
                      );
                    })}
                    {(() => {
                      const s = summary[emp.id] || {};
                      return (
                        <>
                          <td className="text-center px-2 font-mono border-l-2 border-slate-300 font-bold text-emerald-700">{(s.tam || 0) + (s.yarim || 0)}</td>
                          <td className="text-center px-2 font-mono text-[#1B4776] font-bold">{s.mesai || 0}</td>
                          <td className="text-center px-2 font-mono text-blue-600">{s.izin || 0}</td>
                          <td className="text-center px-2 font-mono text-red-600">{s.rapor || 0}</td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-3 text-[10px] text-slate-500">
        Puantaj otomatik kaydedilir. Her tıklama sıradaki tipi seçer: Tam → Yarım → Mesai → İzinli → Raporlu → Boş.
      </div>
    </Shell>
  );
}
